/**
 * Discriminator values for the consolidated managed-catalog table
 * (`catalog_value`). Each value identifies one logical lookup catalog. New
 * catalogs are added here + in CATALOG_TYPE_REGISTRY — no new table or
 * migration required (PD-14226, Option A). Stored as a varchar so the set
 * can grow without a schema change.
 */
export enum CatalogType {
	MerchandiserManufacturer = 'merchandiser_manufacturer',
	MerchandiserModel = 'merchandiser_model',
	/**
	 * Tenant-managed list of client business types (PD-14326). Unlike the
	 * merchandiser catalogs, this one is a SUGGESTION source only —
	 * `client.businessType` stays free-text with no FK, so the integration and
	 * manual entry keep writing raw strings. The catalog values simply seed the
	 * client-form Business Type typeahead alongside values already on clients.
	 */
	ClientBusinessType = 'client_business_type',
	/** Vehicle make (flat parent of vehicle model). PD-14338. */
	VehicleMake = 'vehicle_make',
	/** Vehicle model — child of a make, like merchandiser model. PD-14338. */
	VehicleModel = 'vehicle_model',
	/**
	 * Tenant-managed softphone call dispositions (PD-14436) — the outcomes a
	 * rep can pick in the post-call wrap-up panel. The chosen value's NAME is
	 * stored on `call.dispositionCode` (free varchar), so legacy free-text
	 * codes keep rendering and renames only affect future calls. Seeded with
	 * seven generic defaults per tenant.
	 */
	SoftphoneCallDisposition = 'softphone_call_disposition',
	/**
	 * Dispatch label lists (PD-16048), moved here from the
	 * `distribution-config` preference so every managed value list is edited
	 * on one page. The value NAME is what the driver flows show and, for the
	 * adjustment and stop-outcome reasons, what gets stored verbatim on the
	 * consuming row (`inventory_movement.reason`, `trip_stop.terminationReason`).
	 * A pause stop stores `catalogValueCode(name)` on `trip_stop.pauseCategory`
	 * (varchar 40), which for the seeded defaults is the same code the
	 * preference used to hold (`meal`, `fuel`, ...).
	 */
	DispatchAdjustmentReason = 'dispatch_adjustment_reason',
	DispatchStopOutcomeReason = 'dispatch_stop_outcome_reason',
	DispatchPauseCategory = 'dispatch_pause_category',
	/**
	 * PD-16801 — how a client visit was made (On-site, Phone, ...) and what
	 * came of it. The chosen value's NAME is stored on
	 * `m_sm_client_visit.visitType` / `.outcome` (free varchar), like call
	 * dispositions, so renames only affect future visits.
	 */
	ClientVisitType = 'client_visit_type',
	ClientVisitOutcome = 'client_visit_outcome',
	/**
	 * PD-17101 — why an opportunity (and, from PD-17102, a quote) was lost.
	 * Stored by id (`fk_catalog_value__id_lostReason`) so the Win/Loss report
	 * groups by reason; a rename applies to past losses too.
	 */
	OpportunityLostReason = 'opportunity_lost_reason',
	/**
	 * PD-17109 — where a company on a project stands (Bidding, Awarded,
	 * Working, ...). Stored by id on `project_participant`; each value's
	 * `outcome` maps it to a fixed PROJECT_PARTICIPANT_STATUS_OUTCOMES value.
	 */
	ProjectParticipantStatus = 'project_participant_status',
	/**
	 * PD-17109 — where a competitor on a project or a job stands (Quoting,
	 * Supplying, Won, Lost). One list for both; stored by id on
	 * `project_competitor` and `job_competitor`; each value's `outcome` maps it
	 * to a fixed COMPETITOR_ENGAGEMENT_STATUS_OUTCOMES value.
	 */
	CompetitorEngagementStatus = 'competitor_engagement_status',
}
